var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
import { track } from '../utils/analytics';
import { getMessageBus, MessageBusTopic } from '../messageBus';
import { createSearchModule } from './module';
import { unmountComponentAtNode } from 'react-dom';
const initialize = ({ locale, translations, jurisdiction, environment }) => {
    // Obtain environment details
    const messageBus = getMessageBus();
    const trackingPayload = {
        SearchTerm: '',
        SearchCategory: '',
        SearchSource: '',
    };
    const itemsMemory = []; // eslint-disable-line @typescript-eslint/no-explicit-any
    // Ensure the Message Bus is available
    if (!messageBus) {
        throw new Error('WH MessageBus is not available!');
    }
    // Instantiate search module
    const searchModule = createSearchModule({
        cachePrefix: '$whSearch',
        jurisdiction,
        environment,
        locale,
        minQueryLength: 2,
        maxPreviousQueries: 5,
        maxPopularQueries: 10,
        supportSportFilter: true,
        featuredSports: ['OB_SP9', 'OB_SP43'],
        translations,
    });
    // Handle redirect
    // TODO: Sometimes redirect without message bus?
    function redirect({ url }) {
        window.location.hash = '';
        // wait some time to let hash clear and model to close (probably causing page to refresh)
        setTimeout(() => {
            if (url.startsWith('/') || url.indexOf('/betting/en-gb') > -1) {
                messageBus.publish(MessageBusTopic.internalRedirect, { url });
            }
            else {
                window.location.href = url;
            }
        }, 25);
    }
    // Connect odds format with message bus
    const oddsFormatChangedSubscriptionToken = messageBus.subscribe(MessageBusTopic.oddsFormatChanged, (_, format) => {
        searchModule.setPriceFormat(format);
    });
    const betslipOddsFormatChangedSubscriptionToken = messageBus.subscribe(MessageBusTopic.betslipOddsFormatChanged, (_, format) => {
        searchModule.setPriceFormat(format);
    });
    // Connect to the search-related options in message bus
    // TODO: Support other search-related topics
    //       @see {@link https://gitlab.com/williamhillplc/sports/sportsbook-platform/sb-search-overlay/-/blob/master/src/MessageBusTopics.ts}
    const searchOpenSubscriptionToken = messageBus.subscribe(MessageBusTopic.searchOpen, () => searchModule.setOpen(true));
    const searchOpenNativeSubscriptionToken = messageBus.subscribe(MessageBusTopic.searchOpenNative, () => searchModule.setOpen(true));
    searchModule.on('close', () => messageBus.publish(MessageBusTopic.searchClose));
    searchModule.on('open', () => messageBus.publish(MessageBusTopic.searchOpen));
    // Connect navigation with message bus
    const searchCloseSubscriptionToken = messageBus.subscribe(MessageBusTopic.searchClose, () => searchModule.setOpen(false));
    const pageChangedSubscriptionToken = messageBus.subscribe(MessageBusTopic.pageChanged, () => searchModule.setOpen(false));
    // Load data from betslip accordingly
    const betslipSelectionsChangedSubscriptionToken = messageBus.subscribe(MessageBusTopic.betslipSelectionsChanged, (_, result) => {
        const selections = Object.keys(result)
            .map((id) => result[id])
            .map((bet) => {
            var _a;
            return ({
                id: `OB_OU${bet.selectionId}`,
                priceType: (_a = bet.prices[bet.selectedPriceIndex]) === null || _a === void 0 ? void 0 : _a.type,
            });
        })
            .reduce((selections, leg) => (Object.assign(Object.assign({}, selections), { [leg.id]: {
                sp: leg.priceType === 'STARTING_PRICE',
                fixed: leg.priceType !== 'STARTING_PRICE',
            } })), {});
        // Save in search module
        searchModule.setSelectedSelections(selections);
    });
    // Notify betslip about new selections
    searchModule.on('oddClicked', (odd) => __awaiter(void 0, void 0, void 0, function* () {
        const priceType = odd.sp ? 'STARTING_PRICE' : 'FIXED_ODDS';
        const selectionId = odd.selection.id.replace(/^OB_OU/, '');
        const positionOfSelection = odd.position;
        const searchSection = odd.searchSection;
        let navigationPosition = '';
        if (positionOfSelection) {
            navigationPosition = positionOfSelection.toString();
        }
        const getAnalyticsObject = (searchTerm, navigationPosition, searchSection) => {
            return {
                BetListName: ['Search'],
                BetListPosition: [navigationPosition],
                FeatureDescription: [searchTerm],
                ElementType: 'Advanced',
                SearchSection: [searchSection],
            };
        };
        if (odd.status) {
            messageBus.publish(MessageBusTopic.betslipLegAdd, {
                priceType,
                id: selectionId,
                analytics: getAnalyticsObject(trackingPayload.SearchTerm, navigationPosition, searchSection),
            });
            resetAttempts();
        }
        else {
            messageBus.publish(MessageBusTopic.betslipLegRemove, selectionId);
        }
        const sendId = odd.matchId ? odd.matchId : odd.selection.id;
        yield searchModule.sendInsightsEvent({ id: sendId, eventType: 'conversion' });
    }));
    // Analytics
    const commonAnalyticsData = {
        NonInteractionEvent: '0',
        EventCategory: 'Sport Search',
        ElementType: 'Advanced',
    };
    // Bet Search Open
    searchModule.on('open', () => {
        track('link', Object.assign(Object.assign({ tealium_event: 'sports_search_open' }, commonAnalyticsData), { EventAction: 'Search Open', EventLabel: 'Sport Search Open' }), jurisdiction);
    });
    // Bet Search Results
    let attempts = 0;
    const resetAttempts = () => {
        attempts = 0;
    };
    const getCategoriesType = (casinogame) => {
        if (casinogame) {
            return 'Mixed';
        }
        return 'Sports';
    };
    const trackResults = ({ query, items, categories, }) => {
        attempts++;
        itemsMemory.length = 0;
        itemsMemory.push(...items);
        const searchCategory = getCategoriesType(categories.casinogame);
        trackingPayload.SearchTerm = query;
        trackingPayload.SearchCategory = searchCategory;
        track('link', Object.assign(Object.assign(Object.assign({ tealium_event: 'sports_results' }, commonAnalyticsData), trackingPayload), { EventAction: 'Search Results', EventLabel: 'Sport Search Results', EffortAttempt: attempts, ResultsAmount: items.length }), jurisdiction);
    };
    const trackRedirection = ({ url, id, searchSection, position, }) => {
        resetAttempts();
        if (!id)
            return;
        const item = itemsMemory.find((element) => {
            if (element.value.id === id)
                return element;
        });
        if (!item)
            return;
        switch (searchSection) {
            case 'Selections': {
                track('link', Object.assign(Object.assign(Object.assign({ tealium_event: 'sports_search_click' }, commonAnalyticsData), trackingPayload), { EventAction: 'Search Click', EventLabel: item.value.name, NavigationPosition: position, SearchSection: searchSection, ResultsAmount: itemsMemory.length, EventName: item.value.name, RedirectUrl: url }), jurisdiction);
                break;
            }
            case 'Help': {
                track('link', Object.assign(Object.assign(Object.assign({ tealium_event: 'sports_search_click' }, commonAnalyticsData), trackingPayload), { EventAction: 'Search Click', EventLabel: item.value.title, NavigationPosition: position, SearchSection: searchSection, ResultsAmount: itemsMemory.length, EventName: item.value.title, RedirectUrl: item.value.url }), jurisdiction);
                break;
            }
            case 'Gaming': {
                track('link', Object.assign(Object.assign(Object.assign({ tealium_event: 'sports_search_click' }, commonAnalyticsData), trackingPayload), { EventAction: 'Search Click', EventLabel: item.value.title, NavigationPosition: position, SearchSection: searchSection, ResultsAmount: itemsMemory.length, EventName: item.value.title, RedirectUrl: url }), jurisdiction);
                track('link', {
                    tealium_event: 'game_click',
                    NonInteractionEvent: '0',
                    EventCategory: 'Game Interactions',
                    EventAction: 'Game Launch',
                    EventLabel: item.value.title,
                    ElementType: 'Standard',
                    GameName: item.value.title,
                    GameCode: item.value.code,
                    GameCategory: `Gaming/Sports/${item.value.category}/Search/${item.value.provider}`,
                    GameSupplier: item.value.provider,
                    GameVariant: 'normal',
                    GameList: 'Search Results',
                    GameListPosition: [position === null || position === void 0 ? void 0 : position.toString()],
                    ProductName: 'Sports',
                    ProductAreaName: 'Gaming',
                    SubProductName: 'NA',
                    GameTypeName: 'NA',
                    GameClassName: item.value.category,
                    SlotsType: null,
                    SlotsStartTime: null,
                }, jurisdiction);
                break;
            }
            default: {
                track('link', Object.assign(Object.assign(Object.assign({ tealium_event: 'sports_search_click' }, commonAnalyticsData), trackingPayload), { EventAction: 'Search Click', EventLabel: item.value.name, NavigationPosition: position, SearchSection: searchSection, ResultsAmount: itemsMemory.length, EventName: item.value.name, RedirectUrl: url, EventDate: item.value.startTime }), jurisdiction);
                break;
            }
        }
    };
    const setSource = ({ source }) => {
        trackingPayload.SearchSource = source;
    };
    const onRedirect = ({ url, id, searchSection, position, }) => __awaiter(void 0, void 0, void 0, function* () {
        trackRedirection({
            url,
            id,
            searchSection,
            position,
        });
        // skip insights event sending if popular or previous searches item was clicked
        if (id && position) {
            yield searchModule.sendInsightsEvent({ id, position, eventType: 'click' });
        }
        redirect({ url });
        searchModule.emit('close');
    });
    const onOuterRedirect = ({ url, id, searchSection, position, }) => __awaiter(void 0, void 0, void 0, function* () {
        trackRedirection({
            url,
            id,
            searchSection,
            position,
        });
        // skip insights event sending if popular or previous searches item was clicked
        if (id && position) {
            yield searchModule.sendInsightsEvent({ id, position, eventType: 'click' });
        }
    });
    searchModule.on('open', resetAttempts);
    searchModule.on('close', resetAttempts);
    searchModule.on('source', setSource);
    searchModule.on('results', trackResults);
    searchModule.on('redirect', onRedirect);
    searchModule.on('outer_redirect', onOuterRedirect);
    // Inject Search UI into application
    const element = document.querySelector('#advanced-search-root') || document.createElement('div');
    element.className = 'WhSearch-sbWrapper';
    if (!element.parentNode) {
        document.body.appendChild(element);
    }
    searchModule.render(element);
    // Inform Sportsbook that the Advanced Search has been initialised
    messageBus.publish(MessageBusTopic.advancedSearchInitialized);
    return () => {
        messageBus.unsubscribe(oddsFormatChangedSubscriptionToken);
        messageBus.unsubscribe(betslipOddsFormatChangedSubscriptionToken);
        messageBus.unsubscribe(searchOpenSubscriptionToken);
        messageBus.unsubscribe(searchOpenNativeSubscriptionToken);
        messageBus.unsubscribe(searchCloseSubscriptionToken);
        messageBus.unsubscribe(pageChangedSubscriptionToken);
        messageBus.unsubscribe(betslipSelectionsChangedSubscriptionToken);
        searchModule.clearListeners();
        unmountComponentAtNode(element);
        element.remove();
    };
};
export default initialize;
//# sourceMappingURL=sportsbook.js.map